import { z } from 'zod';
import { mediaIdSchema, mediaTypeSchema, type MediaType } from './content';
import { addWatchlistSchema, type MediaSnapshot } from './library';
import { createPartySchema, type PartyMedia } from './party';
import { DEFAULT_AVATAR } from './constants';

export const FRIEND_CODE_PATTERN = /^[A-HJ-NP-Z2-9]{4}-[A-HJ-NP-Z2-9]{4}$/;
export const MAX_FRIENDS = 200;

export const ratingInputSchema = z.object({
  contentType: mediaTypeSchema,
  stars: z.number().int().min(1).max(5).nullable(),
  note: z
    .string()
    .trim()
    .max(2000, 'Notes can be up to 2000 characters')
    .nullable()
    .transform((s) => (s ? s : null)),
  favorite: z.boolean(),
  shared: z.boolean(),
  snapshot: addWatchlistSchema.shape.snapshot,
});
export type RatingInput = z.input<typeof ratingInputSchema>;

export interface MyRating {
  mediaId: string;
  contentType: MediaType;
  stars: number | null;
  note: string | null;
  favorite: boolean;
  shared: boolean;
  snapshot: MediaSnapshot | null;
  updatedAt: string;
}

export const friendCodeSchema = z
  .string()
  .transform((s) => s.trim().toUpperCase().replace(/\s+/g, ''))
  .transform((s) => (/^[A-Z0-9]{8}$/.test(s) ? `${s.slice(0, 4)}-${s.slice(4)}` : s))
  .pipe(z.string().regex(FRIEND_CODE_PATTERN, "That friend code doesn't look right"));

export const recommendSchema = z.object({
  to: z.array(z.string().min(1).max(64)).min(1, 'Pick at least one friend').max(20),
  mediaId: mediaIdSchema,
  contentType: mediaTypeSchema,
  message: z
    .string()
    .trim()
    .max(280)
    .optional()
    .transform((s) => s || null),
  snapshot: addWatchlistSchema.shape.snapshot,
});
export type RecommendInput = z.input<typeof recommendSchema>;

export interface SocialProfile {
  id: string;
  name: string;
  avatar: string;
  avatarUrl: string | null;
  /** Tea+ staff, shown as a tag on friends and friend requests. */
  staff?: boolean;
  /** Tea+ Premium (in testing): a gold plus next to the name. */
  premium?: boolean;
}

export interface FriendRequest {
  id: string;
  profile: SocialProfile;
  createdAt: string;
}

export interface ActivityItem {
  kind: 'watching' | 'watched' | 'rated';
  profile: SocialProfile;
  mediaId: string;
  contentType: MediaType;
  title: string;
  posterUrl: string | null;
  backdropUrl: string | null;
  season: number | null;
  episode: number | null;
  episodeTitle: string | null;
  stars: number | null;
  favorite: boolean;
  note: string | null;
  at: string;
}

/** The parts of Tea+ a friend can be seen browsing. */
export const PRESENCE_PLACES = [
  'home',
  'movies',
  'tv',
  'discover',
  'search',
  'library',
  'friends',
  'party',
  'downloads',
  'addons',
  'settings',
] as const;
export type PresencePlace = (typeof PRESENCE_PLACES)[number];

/** What a friend is doing in Tea+ when they aren't watching, sent by the app as it changes. */
export const presenceSchema = z.discriminatedUnion('state', [
  z.object({ state: z.literal('browsing'), place: z.enum(PRESENCE_PLACES) }),
  z.object({
    state: z.literal('looking'),
    mediaId: mediaIdSchema,
    contentType: mediaTypeSchema,
    title: z.string().trim().min(1).max(200),
  }),
  z.object({ state: z.literal('away') }),
  z.object({ state: z.literal('offline') }),
]);
export type PresenceInput = z.infer<typeof presenceSchema>;
/** A friend's status: online and doing something, or away. Offline friends have none. */
export type FriendStatus = Exclude<PresenceInput, { state: 'offline' }>;

export interface Friend extends SocialProfile {
  friendshipId: string;
  since: string;
  sharesActivity: boolean;
  watching: ActivityItem | null;
  /** What they are doing when not watching; null when offline or not sharing. */
  status?: FriendStatus | null;
}

export interface FriendsOverview {
  code: string;
  shareActivity: boolean;
  friends: Friend[];
  incoming: FriendRequest[];
  outgoing: FriendRequest[];
}

export interface Recommendation {
  id: string;
  from: SocialProfile;
  mediaId: string;
  contentType: MediaType;
  title: string;
  posterUrl: string | null;
  message: string | null;
  createdAt: string;
  seen: boolean;
}

export interface SocialCounts {
  requests: number;
  recommendations: number;
  /** Chat messages not read yet. Missing from servers before chat. */
  messages?: number;
}

export const MAX_CHAT_MESSAGE = 2000;

/** A picture in a chat, after the app has shrunk it. Fits the server's and the app's body limits. */
export const CHAT_IMAGE_MAX_BYTES = 700_000;

/** Words or a GIF, one or the other. */
export const chatMessageSchema = z
  .object({
    body: z
      .string()
      .trim()
      .max(MAX_CHAT_MESSAGE, `Messages can be up to ${MAX_CHAT_MESSAGE} characters`)
      .default(''),
    gifId: z
      .string()
      .regex(/^[A-Za-z0-9]{1,64}$/)
      .optional(),
  })
  .refine((m) => !!m.body !== !!m.gifId, { message: 'Write a message first', path: ['body'] });
export type ChatMessageInput = z.input<typeof chatMessageSchema>;

export const chatImageSchema = z.object({
  image: z
    .string()
    .max(Math.ceil((CHAT_IMAGE_MAX_BYTES * 4) / 3) + 40)
    .regex(/^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/]+=*$/, 'That image could not be used'),
});
export type ChatImageInput = z.input<typeof chatImageSchema>;

/** A GIF to choose from, in the GIF picker. */
export interface ChatGif {
  id: string;
  title: string;
  previewUrl: string;
}

export interface ChatGifResults {
  /** False when this server has no Giphy key. */
  enabled: boolean;
  gifs: ChatGif[];
}

/** One line of a chat: a message, or a title one of you shared with the other. */
export interface ChatItem {
  id: string;
  kind: 'message' | 'title';
  mine: boolean;
  /** The message, or the note sent with a shared title. */
  body: string | null;
  gifUrl: string | null;
  imageUrl: string | null;
  media: {
    mediaId: string;
    contentType: MediaType;
    title: string;
    posterUrl: string | null;
  } | null;
  createdAt: string;
  deliveredAt: string | null;
  readAt: string | null;
}

export interface ChatSummary {
  friend: SocialProfile;
  /** False when this friend has turned chat off, so they can't be messaged. */
  available: boolean;
  last: ChatItem | null;
  unread: number;
}

export interface ChatThread {
  /** Oldest first. */
  items: ChatItem[];
  hasMore: boolean;
  available: boolean;
}

/** The title a notification is about. */
export interface NotificationMedia {
  mediaId: string;
  contentType: MediaType;
  title: string;
  posterUrl: string | null;
  season: number | null;
  episode: number | null;
}

/** Live notifications from friends, sent over /api/notifications/events. */
export type SocialNotification = { id: string; at: string; from: SocialProfile } & (
  | { type: 'recommendation'; media: NotificationMedia; message: string | null }
  | { type: 'watching'; media: NotificationMedia }
  | { type: 'friend-request' }
  | { type: 'friend-accepted' }
  /** A friend opened or closed the player: refresh what they're doing, without a card. */
  | { type: 'presence' }
  | { type: 'chat-message'; messageId: string; body: string; attachment: 'gif' | 'image' | null }
  /** A chat changed (a receipt, or something sent from another window): refresh, without a card. */
  | { type: 'chat-update' }
  | { type: 'join-request'; requestId: string; media: NotificationMedia; expiresAt: string }
  | {
      type: 'join-reply';
      requestId: string;
      accepted: boolean;
      /** The party to join when accepted; live means it is already playing. */
      code: string | null;
      live: boolean;
      media: PartyMedia | null;
    }
  | { type: 'party-invite'; code: string }
  /** Tea+ Premium (in testing): a show you watch has a new episode. From is Tea+ itself. */
  | { type: 'new-episode'; media: NotificationMedia; episodeTitle: string | null }
);

/** Who notifications from Tea+ itself (not a friend) come from. */
export const TEAPLUS_SENDER: SocialProfile = {
  id: 'teaplus',
  name: 'Tea+',
  avatar: DEFAULT_AVATAR,
  avatarUrl: null,
};

export type SocialNotificationInput = SocialNotification extends infer N
  ? N extends SocialNotification
    ? Omit<N, 'id' | 'at'>
    : never
  : never;

/** Accepting a Request to Join: what the host is watching right now, if anything. */
export const joinAcceptSchema = z.object({
  media: createPartySchema.optional(),
  position: z.number().min(0).max(1e6).optional(),
  playing: z.boolean().optional(),
  stream: z.object({ fingerprint: z.string().max(600), title: z.string().max(300) }).optional(),
});
export type JoinAcceptInput = z.input<typeof joinAcceptSchema>;
